import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { getBundleProducts } from 'utils/storefront/get-bundle-products';
import Image from 'utils/image';
import ProductPrice from './product-price';
import ProductBadge from '../product/product-badge';

const ProductCard = ({
  product,
  useBuilderFunctionality,
  showMobileAltImage = false,
  isDesktop,
  index,
  trackingData,
  addToBundleBuilder = null,
  requiredCount = null,
  availableBundleBuilderCategorySlots = null,
  bundleBuilderSelectedProducts = null,
  bundleItemAdded = {},
  setBundleItemAdded = null,
  bundleItemRemoved = {},
  setBundleItemRemoved = null,
}) => {
  const [isAdded, setIsAdded] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [isHovered, setIsHovered] = useState(false); // State to track hover
  const url = `${Shopify.routes.root}products/${product.handle}`;
  const indicatorRef = useRef(null);
  const firstCardRef = useRef(null);
  const cardRef = useRef(null);
  const technicalDataEl = document.querySelector('[data-technical]');
  const data = JSON.parse(technicalDataEl.textContent);

  const [bundleBuilderQtyInput, setBundleBuilderQtyInput] = useState(1);
  const [qtyInBundle, setQtyInBundle] = useState(0);

  const bundleBuilderProductId = parseInt(product.id.split('/').pop(), 10);

  if (useBuilderFunctionality) {
    let findThisProductInBundleBuilder;
    if (bundleBuilderSelectedProducts?.length) {
      findThisProductInBundleBuilder = bundleBuilderSelectedProducts.filter(
        (p) => parseInt(p.id, 10) === bundleBuilderProductId,
      );

      // set the quantity displayed on the Add button
      setTimeout(() => {
        setQtyInBundle(findThisProductInBundleBuilder.length);
      }, 100);

      // added
      if (bundleBuilderProductId === bundleItemAdded?.id) {
        setBundleItemAdded({ id: 0, multi: null });
      }

      // removed
      if (bundleBuilderProductId === bundleItemRemoved?.id) {
        if (findThisProductInBundleBuilder.length < 1) setIsAdded(false);
        setBundleItemRemoved({ id: 0, multi: null });
      }
    } else {
      setIsAdded(false);
    }
  }

  useEffect(() => {
    const isBundle = product.tags?.find((tag) => tag === 'bundle');
    if (isBundle) {
      const fetchData = async () => {
        setBundle(await getBundleProducts(product));
      };
      // eslint-disable-next-line no-console
      fetchData().catch(console.error);
    }
  }, []);

  const animateFirstCard = () => {
    setTimeout(() => {
      firstCardRef.current.classList.add('animateCard');
    }, 1000);
  };

  useEffect(() => {
    if (window.yotpoWidgetsContainer?.initWidgets) {
      window.yotpoWidgetsContainer.initWidgets();
    }
    if (!isDesktop && showMobileAltImage && firstCardRef.current) {
      animateFirstCard();
    }
  }, []);

  const handleClick = (e) => {
    if (!trackingData) return;
    e.preventDefault();
    /* eslint-disable no-param-reassign */
    trackingData = {
      ...trackingData,
      destinationPage: `${window.location.href}${url}`,
      contentText: `${product.title} - ${product.variants[0]?.sku}`,
    };
    /* eslint-enable no-param-reassign */

    Shopify.analytics.publish('home_content_click', trackingData);
    window.location.href = url;
  };

  const handleScroll = (e) => {
    if (isDesktop || !showMobileAltImage) return;
    const position = e.target.scrollLeft;
    indicatorRef.current.style.transform = `translateX(${Math.ceil((position * 70) / e.target.offsetWidth)}%)`;
  };

  const isProductAvailable = bundle ? bundle.available : product.variants[0].availableForSale;

  const handleBundleBuilderAdd = (event) => {
    // if required amount of bundle items to achieve promo === the number of items already selected
    if (requiredCount === bundleBuilderSelectedProducts.length) return;

    const { prodTitle, bundleBuilderPid, variantId, collection, uid } = event.target.parentElement.dataset;

    if (!Number.isNaN(bundleBuilderQtyInput)) {
      addToBundleBuilder(prodTitle, bundleBuilderPid, bundleBuilderQtyInput, variantId, collection, uid);
    } else {
      addToBundleBuilder(prodTitle, bundleBuilderPid, 1, variantId, collection, uid);
    }

    setIsAdded(true);
  };

  const handleBundleBuilderQtyInputChange = (event) => {
    const value = parseInt(event.target.value, 10);

    if (!Number.isNaN(value)) {
      if (value > availableBundleBuilderCategorySlots) {
        setBundleBuilderQtyInput(availableBundleBuilderCategorySlots);
      } else {
        setBundleBuilderQtyInput(value);
      }
    } else {
      setBundleBuilderQtyInput(1);
    }
  };

  const handleBundleBuilderQtyBtnClick = (action) => {
    if (requiredCount === bundleBuilderSelectedProducts.length) return;

    if (action === 'add') {
      if (bundleBuilderQtyInput >= availableBundleBuilderCategorySlots) {
        setBundleBuilderQtyInput(availableBundleBuilderCategorySlots);
      } else {
        setBundleBuilderQtyInput((prevState) => prevState + 1);
      }
    } else {
      if (bundleBuilderQtyInput === 1) return;
      setBundleBuilderQtyInput((prevState) => prevState - 1);
    }
  };

  return (
    <article
      className={`relative product-card flex flex-col h-full p-px ${showMobileAltImage ? 'overflow-hidden' : ''}`}
      ref={cardRef}
      data-handle={`${product.handle}`}
    >
      <a
        className="relative product-card-container group block no-underline overflow-hidden test"
        href={url}
        ref={index === 0 ? firstCardRef : null}
        aria-label={`${product.title}`}
        onClick={(e) => handleClick(e, product, url)}
      >
        <div
          // eslint-disable-next-line max-len
          className={`relative aspect-[163/186] mb-3 lg:aspect-[317/346] ${
            showMobileAltImage && !isDesktop
              ? 'flex overflow-x-auto lg:static scrollbar-hide snap-mandatory snap-x'
              : ''
          }`}
          onScroll={(e) => handleScroll(e)}
        >
          {product.images[0] && (
            <span
              className={`
              ${
                showMobileAltImage && !isDesktop
                  ? 'min-w-full h-full opacity-100 relative snap-start'
                  : product.images[1] && isDesktop
                  ? 'opacity:100 group-hover:opacity-0 transition-opacity duration-500'
                  : 'opacity:100'
              }`}
            >
              <ProductBadge
                tags={product.tags}
                className={'nosto-badge bg-pantone-8 absolute z-10 flex items-center text-nowrap left-[4px]'}
              />
              <Image
                {...product.images[0]}
                srcWidths={{ sm: 640 }}
                classes="absolute left-0 top-0 w-full h-full object-cover"
              />
            </span>
          )}
          {product.images[1] && isDesktop && (
            <span
              className={`${
                showMobileAltImage && !isDesktop
                  ? 'block relative min-w-full snap-start'
                  : 'absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-1'
              }`}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {isHovered && (
                <Image
                  {...product.images[1]}
                  srcWidths={{ sm: 640 }}
                  classes="absolute left-0 top-0 w-full h-full object-cover"
                  loading="lazy"
                />
              )}
            </span>
          )}
        </div>
        {showMobileAltImage && product.images.length > 1 && !isDesktop && (
          <div
            className="absolute h-[3px] w-5 bg-grey-4 left-2.5 
                          bottom-5 rounded-lg overflow-hidden"
          >
            <span className="indicator block h-[3px] w-3 bg-black rounded-lg" ref={indicatorRef} />
          </div>
        )}
      </a>

      <div className="flex flex-col justify-between flex-1 z-2 relative">
        <div className="w-full relative product-card-container pb-[20px]">
          <a className="group block leading-5 hover:underline" href={url} onClick={(e) => handleClick(e)}>
            <h3 className="text-headline-tag">{product.title}</h3>

            {product.subtitle && <p className="text-body-light">{product.subtitle}</p>}
          </a>

          <p className="text-body-2 mb-2.5">
            <ProductPrice product={product} />
          </p>

          <div
            className="yotpo-widget-instance"
            data-yotpo-instance-id={data.yotpoBottomlineId}
            data-yotpo-product-id={product.id.split('Product/')[1]}
            data-yotpo-cart-product-id={product.id.split('Product/')[1]}
            data-yotpo-section-id="collection"
          ></div>
        </div>

        {useBuilderFunctionality && (
          <div
            data-bundle-builder-pid={bundleBuilderProductId}
            data-variant-id={product.variants[0].id}
            data-prod-title={product.title}
            data-collection={product.collection_id || ''}
            data-uid={product.uid || ''}
          >
            {requiredCount > 1 && (
              <div className="flex items-center border border-black p-2 w-max mt-2.5 lg:mt-5">
                <button
                  type="button"
                  aria-label="Remove"
                  className="w-[27px] h-[27px] text-center text-black font-bold disabled:opacity-50"
                  onClick={() => handleBundleBuilderQtyBtnClick('minus')}
                  disabled={requiredCount === bundleBuilderSelectedProducts.length}
                >
                  −
                </button>

                <input
                  type="number"
                  max={availableBundleBuilderCategorySlots}
                  value={bundleBuilderQtyInput}
                  className="w-[25px] h-[27px] px-2 border-0 text-center text-black disabled:opacity-50"
                  onChange={handleBundleBuilderQtyInputChange}
                  disabled={requiredCount === bundleBuilderSelectedProducts.length}
                />

                <button
                  type="button"
                  aria-label="Add"
                  className="w-[27px] h-[27px] text-center text-black font-bold disabled:opacity-50"
                  onClick={() => handleBundleBuilderQtyBtnClick('add')}
                  disabled={requiredCount === bundleBuilderSelectedProducts.length}
                >
                  +
                </button>
              </div>
            )}

            <button
              className={`bundle-builder-btn button mt-2.5 w-full lg:mt-[22px]
              ${!isProductAvailable ? 'pointer-events-none' : ''}
              ${isAdded ? 'button--primary disabled:!opacity-100' : 'button--secondary'}
              `}
              onClick={handleBundleBuilderAdd}
              disabled={requiredCount === bundleBuilderSelectedProducts.length || !isProductAvailable}
            >
              {isAdded
                ? `${window.TMW.Strings.product.added} (${qtyInBundle})`
                : !isProductAvailable
                ? window.TMW.Strings.product.outOfStock
                : 'add'}
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

export default ProductCard;
